import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Pagination,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import theme from "../utils/theme";
import { modalStyles } from "./Common/ModalTemplate";
import { formatDateTimeDMY } from "../utils/utilityFunctions";
import EllipsizedText from "./Common/EllipsizedText";
import useActivityLogs from "../hooks/useActivityLogs";
import IconButtons from "./IconButtons";

const styles = {
  tableComponent: {
    position: "relative",
    height: { xs: 380, sm: 440, md: 520 },
    minHeight: { xs: 380, sm: 440, md: 520 },
    overflowY: "auto",
  },
  tableHeaderCell: {
    color: theme.palette.text.light,
    fontWeight: 800,
    backgroundColor: theme.palette.background.dark,
    zIndex: 2,
    cursor: "pointer",
    userSelect: "none",
    maxWidth: { xs: 90, sm: 120, md: 180 },
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    p: { xs: 1, sm: 1.5 },
  },
  tableCell: {
    px: 1,
    py: 1,
    width: "fit-content",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const toAbsoluteUrl = (url) =>
  url?.startsWith("http") ? url : `${API_BASE}${url || ""}`;

const ActivityLog = ({ open, onClose, rfiNo, type = "dimensional" }) => {
  // Data & pagination via hook for clarity and reuse
  const { page, setPage, isLoading, logs, totalPages } = useActivityLogs({
    rfiNo,
    open,
    limit: 10,
    type,
  });

  // Pagination handler
  const handlePageChange = (event, newPage) => setPage(newPage);

  // Memoize formatted rows to keep JSX minimal in the render path
  const formattedLogs = React.useMemo(() => {
    return (logs || []).map((row, idx) => {
      const performerName = row?.performer
        ? `${row.performer.first_name || ""} ${
            row.performer.last_name || ""
          }`.trim()
        : "—";
      const actionType = (row?.action_type || "").toLowerCase();
      const actionLabel = (row?.action_type || "")
        .toUpperCase()
        .replaceAll("_", " ");
      const href = row?.file_url ? toAbsoluteUrl(row.file_url) : null;
      const fileName =
        row?.file_name ||
        (row?.file_url ? row.file_url.split("/").pop() : null);
      return {
        key: row.log_id || `${row.action_type}-${idx}`,
        idx,
        performerName,
        actionType,
        actionLabel,
        href,
        fileName,
        oldRemarks: row?.old_remarks,
        newRemarks: row?.new_remarks,
        updatedAt: row?.action_timestamp
          ? formatDateTimeDMY(row.action_timestamp)
          : null,
      };
    });
  }, [logs]);

  // Helper to render file name + view icon
  const renderFile = (name, link) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <EllipsizedText text={name} maxWidth={220} />
      {link && (
        <Tooltip title="View document">
          <Box
            component="a"
            href={link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconButtons size="small" buttonType="view" />
          </Box>
        </Tooltip>
      )}
    </Box>
  );

  // Centered dash for missing values
  const renderDash = (
    <Box sx={{ width: "100%", textAlign: "center", color: "text.secondary" }}>
      —
    </Box>
  );

  // Column configuration for headers and cells
  const columns = [
    {
      header: "Action",
      key: "action",
      render: (r) => r.actionLabel || renderDash,
      headerSx: styles.tableHeaderCell,
      cellSx: styles.tableCell,
    },
    {
      header: "Old Value",
      key: "oldValue",
      render: (r) => {
        const isDeleted = r.actionType === "document_deleted";
        if (isDeleted && (r.href || r.fileName))
          return renderFile(r.fileName, r.href);
        if (r.oldRemarks)
          return <EllipsizedText text={r.oldRemarks} maxWidth={300} />;
        return renderDash;
      },
      headerSx: styles.tableHeaderCell,
      cellSx: styles.tableCell,
    },
    {
      header: "New Value",
      key: "newValue",
      render: (r) => {
        const isAdded = r.actionType === "document_added";
        const isUpdated = r.actionType === "document_updated";
        if ((isAdded || isUpdated) && (r.href || r.fileName))
          return renderFile(r.fileName, r.href);
        if (r.newRemarks)
          return <EllipsizedText text={r.newRemarks} maxWidth={300} />;
        return renderDash;
      },
      headerSx: styles.tableHeaderCell,
      cellSx: styles.tableCell,
    },
    {
      header: "Performed By",
      key: "performedBy",
      render: (r) => r.performerName || renderDash,
      headerSx: styles.tableHeaderCell,
      cellSx: styles.tableCell,
    },
    {
      header: "Updated At",
      key: "updatedAt",
      render: (r) => r.updatedAt || renderDash,
      headerSx: styles.tableHeaderCell,
      cellSx: styles.tableCell,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ 
        sx: { 
          ...modalStyles.dialogPaper,
          width: "90vw",
          maxWidth: "1800px",
          height: "85vh",
          maxHeight: "900px",
        } 
      }}
      aria-labelledby="activity-log-dialog-title"
    >
      <DialogTitle id="activity-log-dialog-title" sx={modalStyles.dialogTitle}>
        <Typography variant="h6" sx={modalStyles.dialogTitleText}>
          Activity Log for RFI-{rfiNo}
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={modalStyles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={modalStyles.dialogContent}>
        <TableContainer component={Paper} sx={styles.tableComponent}>
          <Table stickyHeader size="small" aria-label="activity log table">
            <TableHead>
              <TableRow
                hover
                key="header"
                sx={{
                  backgroundColor: theme.palette.background.dark,
                  zIndex: 2,
                  height: { xs: 44, sm: 48 },
                  "& .MuiTableCell-root": { py: 1 },
                }}
              >
                {columns.map((col) => (
                  <TableCell key={col.key} sx={col.headerSx}>
                    <span
                      style={{
                        display: "inline-block",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "bottom",
                      }}
                    >
                      {col.header}
                    </span>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? null : formattedLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No activity found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                formattedLogs.map((r) => (
                  <TableRow
                    key={r.key}
                    hover
                    sx={{
                      height: { xs: 44, sm: 48 },
                      "& .MuiTableCell-root": { py: 1 },
                      bgcolor:
                        r.idx % 2 === 0
                          ? theme.palette.background.paper
                          : theme.palette.background.default,
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.key} sx={col.cellSx}>
                        {col.render(r)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 3,
                bgcolor: "transparent",
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
        </TableContainer>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
export default ActivityLog;
